import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TuneIcon from '@mui/icons-material/Tune';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ChangeTPeriodModal from './ChangeTPeriodModal';
import ChangePassPeriodModal from './ChangePassPeriodModal';
import ChangeTrialPeriodModal from './ChangeTrialPeriodModal';
import ReservationSettingModal from './ReservationSettingModal';
import AutoDeductionSettingModal, { useAutoDeductionVisible } from './AutoDeductionSettingModal';
import TicketShareTab from './TicketShareTab';
import { AccessDoorPanel, useAccessDoorAvailable } from '../cards/AccessDoorRow';
import { ModalContainer, ModalHeader, ModalTitle, CloseButton } from '../../ChangeTFreezyModalStyled';

const HOLDING = {
  course: { type: 'TICKET', idKey: 'memberCourseTicketId', nameKey: 'ticketName' },
  pass: { type: 'PASS', idKey: 'memberCoursePassId', nameKey: 'ticketName' },
  trial: { type: 'TRIAL', idKey: 'memberTrialTicketId', nameKey: 'trialName' },
};

const PRIMARY = '#4AB3BC';

// 수강권 공유(#154)는 개인·그룹 수강권만(듀엣 제외).
const SHAREABLE_CLASS_TYPES = ['PRIVATE_LESSON', 'GROUP_LESSON'];

/**
 * 회원 상세 회원권 카드의 "상품 정보 수정" — 기간·횟수 / 예약 설정 / 자동 차감 / 출입문 / 공유 를 한 모달의 탭으로 묶는다.
 * 각 탭은 기존 모달을 embedded 로 렌더해 저장 API·검증을 그대로 쓴다(탭마다 저장 버튼 독립).
 * 예약 설정 탭은 수강권이면 항상, 출입문 탭은 출입문 체계로 전환된 지점만 나타난다.
 */
const ProductInfoEditModal = ({ modal, setModal, bigUser, ticket, user, getAllTickets, kind = 'course', initialTab = 'period', expired = false }) => {
  const holding = HOLDING[kind] ?? HOLDING.course;
  const holdingId = ticket?.[holding.idKey];
  const doorAvailable = useAccessDoorAvailable(holding.type, holdingId);
  // 예약 설정(일일/주간 제한·최대 예약권·취소권)은 수강권이면 유형과 무관하게 쓴다.
  // 예전 카드에서 그룹레슨만 버튼이 있던 건 버튼 칸 자리 문제였지 기능 제한이 아니었다.
  const reservationAvailable = kind === 'course';
  // 공유 설정은 구매한 회원(소유자) 카드에서만 — 공유받은 카드(sharedFrom)에는 탭이 없다.
  // 자동 차감은 만료 전 수강권만, 기업회원 자동 차감 설정이 켜진 곳(SUPERADMIN 은 항상)에서만. 꺼진 회원권도 다시 켤 수 있게 탭은 늘 띄운다.
  const autoDeductionVisible = useAutoDeductionVisible();
  const autoDeductionAvailable = kind === 'course' && !expired && autoDeductionVisible;
  const shareAvailable = kind === 'course' && SHAREABLE_CLASS_TYPES.includes(ticket?.classType) && !ticket?.sharedFrom;

  const tabs = [
    { key: 'period', label: kind === 'pass' ? '이용 기간' : '기간 · 횟수', icon: <EventNoteIcon sx={{ fontSize: 18 }} /> },
    ...(reservationAvailable ? [{ key: 'reservation', label: '예약 설정', icon: <TuneIcon sx={{ fontSize: 18 }} /> }] : []),
    ...(autoDeductionAvailable ? [{ key: 'autoDeduction', label: '자동 차감', icon: <AutorenewIcon sx={{ fontSize: 18 }} /> }] : []),
    ...(doorAvailable ? [{ key: 'door', label: '출입문', icon: <MeetingRoomIcon sx={{ fontSize: 18 }} /> }] : []),
    ...(shareAvailable ? [{ key: 'share', label: '공유', icon: <GroupAddIcon sx={{ fontSize: 18 }} /> }] : []),
  ];
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    if (!tabs.some(t => t.key === tab)) setTab('period');
  }, [tabs.map(t => t.key).join(','), tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClose = () => setModal(prev => ({ ...prev, openProductInfo: false }));
  const onDone = async () => {
    await getAllTickets?.();
    onClose();
  };
  const common = { modal, setModal, bigUser, ticket, user, getAllTickets, embedded: true, onDone };

  return (
    <Modal open={!!modal.openProductInfo} onClose={onClose}>
      <ModalContainer
        sx={{
          width: '820px',
          maxWidth: 'calc(100vw - 48px)',
          // 탭 안 버튼(저장·자동적용 등) 색을 이 모달에서만 PRIMARY 로. 취소(회색)·비활성은 그대로.
          '--modal-primary': PRIMARY,
          '--modal-primary-hover': PRIMARY,
        }}>
        <ModalHeader>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', minWidth: 0 }}>
            <ModalTitle>상품 정보 수정</ModalTitle>
            <Typography
              sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {ticket?.[holding.nameKey]}
            </Typography>
          </Box>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <Box sx={{ display: 'flex', minHeight: 360 }}>
          {/* 좌측 탭 */}
          <Box sx={{ width: 168, flexShrink: 0, background: '#F7F9FA', borderRight: '1px solid #ECEFF1', py: 2 }}>
            {tabs.map(t => {
              const active = t.key === tab;
              return (
                <Box
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mx: 1.5,
                    mb: 0.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                    color: active ? '#0F7F85' : '#555',
                    background: active ? '#E3F2F3' : 'transparent',
                    '&:hover': { background: active ? '#E3F2F3' : '#EEF2F3' },
                  }}>
                  <Box sx={{ color: active ? PRIMARY : '#9E9E9E', display: 'flex' }}>{t.icon}</Box>
                  {t.label}
                </Box>
              );
            })}
          </Box>

          {/* 우측 본문 — 기존 모달 본문을 그대로 embedded 로 */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {tab === 'period' && kind === 'course' && <ChangeTPeriodModal key={holdingId} {...common} />}
            {tab === 'period' && kind === 'pass' && <ChangePassPeriodModal key={holdingId} {...common} />}
            {tab === 'period' && kind === 'trial' && <ChangeTrialPeriodModal key={holdingId} {...common} />}
            {tab === 'reservation' && <ReservationSettingModal key={holdingId} {...common} />}
            {tab === 'autoDeduction' && <AutoDeductionSettingModal key={holdingId} {...common} />}
            {tab === 'share' && <TicketShareTab key={holdingId} {...common} />}
            {tab === 'door' && (
              <Box sx={{ p: 4 }}>
                <AccessDoorPanel holdingType={holding.type} holdingId={holdingId} onDone={onDone} />
              </Box>
            )}
          </Box>
        </Box>
      </ModalContainer>
    </Modal>
  );
};

export default ProductInfoEditModal;
